import { CheckCircle2, Heart, Shield, Flame, Briefcase } from "lucide-react";
import { motion } from "framer-motion";
import { PROGRAMS } from "@/lib/programs";
import { OtherProgramsCTA } from "./SchoolProgram";

const program = PROGRAMS.find((p) => p.slug === "adult");

const FOCUS_AREAS = [
    {
        icon: Flame,
        title: "Look Better",
        text: "Body composition and physique programming for the adult who still wants to look like an athlete — strong, lean, capable.",
    },
    {
        icon: Heart,
        title: "Feel Better",
        text: "Mobility, joint health and recovery work that takes the morning stiffness, the desk-job back, and the nagging old injury off the table.",
    },
    {
        icon: Briefcase,
        title: "Perform Better — In Life",
        text: "Built for working professionals. Energy for the workday, capacity for the weekend, durability for the only body you'll ever have.",
    },
    {
        icon: Shield,
        title: "Train Like You Still Mean It",
        text: "Programming, not workouts. Goals, not vibes. The same athlete-first framework — adjusted for the life you're actually living.",
    },
];

const FEATURES = [
    "Group and small-group coached sessions",
    "Strength + conditioning hybrid programming",
    "Body composition & nutrition guidance",
    "Mobility & joint-health protocols",
    "Event prep — Spartan, marathons, leagues",
    "Schedule built around career & family life",
];

export default function AdultPerformance() {
    return (
        <div data-testid="adult-program-page" className="bg-brand-obsidian">
            <section className="relative min-h-[80svh] flex items-end overflow-hidden">
                <img
                    src={program.image}
                    alt={program.title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.55),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.18),transparent_50%)] pointer-events-none" />
                <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 pt-32 pb-16 md:pb-24">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-5 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" />
                        Program · Ages 22 — 88+
                    </div>
                    <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-wider leading-[0.88] text-white max-w-5xl">
                        ADULT <span className="text-brand-red">PERFORMANCE.</span>
                    </h1>
                    <p className="mt-7 text-zinc-300 text-lg max-w-2xl leading-relaxed">
                        {program.summary}
                    </p>
                </div>
            </section>

            <section className="py-20 md:py-28 border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16">
                    <div className="md:col-span-5">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-2">
                            <span className="inline-block w-8 h-px bg-brand-red" />
                            For the working professional
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white leading-[0.92]">
                            LOOK BETTER.
                            <br />
                            FEEL BETTER.
                            <br />
                            <span className="text-brand-red">PERFORM BETTER.</span>
                        </h2>
                        <p className="mt-7 text-zinc-300 leading-relaxed">
                            Adult Performance is for the working professional who still wants to train like an
                            athlete. The career, the family, the calendar — none of it stops, but the body you
                            live in still has to keep up. We program strength, conditioning, mobility and
                            physique work as one system, built around the life you're actually living — because
                            it's the only body you'll ever have.
                        </p>
                    </div>
                    <div className="md:col-span-7">
                        <div className="grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                            {FOCUS_AREAS.map((f, i) => (
                                <motion.div
                                    key={f.title}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.07 }}
                                    className="bg-black p-7 hover:bg-zinc-950 transition-colors"
                                    data-testid={`adult-focus-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
                                >
                                    <div className={`inline-flex items-center justify-center w-10 h-10 text-white mb-4 ${i % 2 === 0 ? "bg-brand-red" : "bg-brand-navy-700"}`}>
                                        <f.icon className="w-4 h-4" />
                                    </div>
                                    <div className="font-display text-2xl tracking-wider text-white">
                                        {f.title}
                                    </div>
                                    <p className="mt-2 text-sm text-zinc-200 leading-relaxed">{f.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative bg-gradient-to-r from-brand-navy-950 via-black to-black border-y border-zinc-900 py-20 md:py-24 overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_50%,rgba(230,0,0,0.14),transparent_55%)] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="font-display text-6xl md:text-7xl text-brand-red tracking-wider leading-[0.85]">
                            FOR LIFE
                        </div>
                        <h3 className="font-display text-3xl md:text-4xl tracking-wider text-white mt-3">
                            WHAT'S INSIDE THE PROGRAM
                        </h3>
                    </div>
                    <div className="md:col-span-7">
                        <ul className="grid sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                            {FEATURES.map((f, i) => (
                                <li
                                    key={i}
                                    data-testid={`adult-feature-${i}`}
                                    className="bg-black px-5 py-4 flex items-start gap-3"
                                >
                                    <CheckCircle2 className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-zinc-200">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <OtherProgramsCTA currentSlug="adult" />
        </div>
    );
}
